import React, { useEffect, useState } from "react";
import { CalendarDays, ScrollText, CreditCard, Megaphone, ArrowRight, TrendingUp, HeartPulse } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle, Card, Badge, Loading, fmtDate } from "./ui";
import { useAuth } from "../../context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [eng, setEng] = useState(null);

  useEffect(() => {
    api.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  useEffect(() => {
    if (user?.is_officer) {
      api.get("/officer/engagement").then((r) => setEng(r.data.summary)).catch(() => {});
    }
  }, [user]);

  if (!data) return <Loading />;

  return (
    <div data-testid="dashboard-page">
      <PageTitle eyebrow="The Inner Lodge" title="Dashboard" sub="Everything you need to keep the Lodge running." />

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <div className="flex items-center gap-2 text-gold-deep"><CalendarDays size={18} /><span className="font-sans text-xs font-semibold uppercase tracking-widest">Next Summons</span></div>
          {data.next_event ? (
            <>
              <p className="mt-4 font-display text-2xl text-sapphire-deep">{data.next_event.title}</p>
              <p className="mt-1 font-sans text-sm text-ink/55">{fmtDate(data.next_event.date)}</p>
              <p className="font-sans text-sm text-ink/55">{data.next_event.time}</p>
            </>
          ) : (
            <p className="mt-4 font-sans text-sm text-ink/55">No upcoming meetings scheduled.</p>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2 text-gold-deep"><CreditCard size={18} /><span className="font-sans text-xs font-semibold uppercase tracking-widest">Dues Status</span></div>
          {data.dues ? (
            <>
              <div className="mt-4"><Badge>{data.dues.status}</Badge></div>
              <p className="mt-3 font-display text-2xl text-sapphire-deep">FY {data.dues.year}</p>
              <p className="font-sans text-sm text-ink/55">S${data.dues.amount} annual subscription</p>
            </>
          ) : (
            <p className="mt-4 font-sans text-sm text-ink/55">No dues record found.</p>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2 text-gold-deep"><ScrollText size={18} /><span className="font-sans text-xs font-semibold uppercase tracking-widest">Latest Minutes</span></div>
          {data.latest_minutes ? (
            <>
              <p className="mt-4 font-display text-2xl text-sapphire-deep">{data.latest_minutes.title}</p>
              <p className="mt-1 font-sans text-sm text-ink/55">{fmtDate(data.latest_minutes.date)}</p>
            </>
          ) : (
            <p className="mt-4 font-sans text-sm text-ink/55">No minutes posted yet.</p>
          )}
        </Card>
      </div>

      {user?.is_officer && eng && (
        <Card className="mt-6 flex flex-col gap-4 border-gold/30 md:flex-row md:items-center md:justify-between" data-testid="dashboard-officer-kpi">
          <div className="flex flex-wrap items-center gap-8">
            <div>
              <div className="flex items-center gap-2 text-gold-deep"><TrendingUp size={16} /><span className="font-sans text-xs font-semibold uppercase tracking-widest">Avg Attendance</span></div>
              <p className="mt-1 font-display text-3xl text-sapphire-deep">{eng.avg_attendance_rate}%</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-gold-deep"><HeartPulse size={16} /><span className="font-sans text-xs font-semibold uppercase tracking-widest">Need Attention</span></div>
              <p className="mt-1 font-display text-3xl text-garnet">{eng.at_risk + eng.dormant}</p>
            </div>
            <div className="hidden sm:block">
              <span className="font-sans text-xs font-semibold uppercase tracking-widest text-gold-deep">Active</span>
              <p className="mt-1 font-display text-3xl text-emerald">{eng.active}<span className="font-sans text-base text-ink/40">/{eng.total_members}</span></p>
            </div>
          </div>
          <Link to="/portal/engagement" className="flex items-center gap-2 rounded-full bg-sapphire-deep px-5 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-parchment transition hover:bg-sapphire-mid" data-testid="dashboard-view-engagement">
            View Engagement KPIs <ArrowRight size={15} />
          </Link>
        </Card>
      )}

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <Card className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-gold-deep"><Megaphone size={18} /><span className="font-sans text-xs font-semibold uppercase tracking-widest">Announcements</span></div>
            <p className="mt-3 font-display text-3xl text-sapphire-deep">{data.announcement_count ?? 0} <span className="font-sans text-sm text-ink/55">circulars</span></p>
          </div>
          <Link to="/portal/announcements" className="flex items-center gap-1 font-sans text-sm text-gold-deep hover:underline">Read <ArrowRight size={15} /></Link>
        </Card>

        <Card className="flex items-center justify-between">
          <div>
            <span className="font-sans text-xs font-semibold uppercase tracking-widest text-gold-deep">Your Standing</span>
            <p className="mt-3 font-display text-2xl text-sapphire-deep">{user?.rank}</p>
            <p className="font-sans text-sm text-ink/55">Initiated {user?.year_initiated}</p>
          </div>
          <Link to="/portal/directory" className="flex items-center gap-1 font-sans text-sm text-gold-deep hover:underline">Directory <ArrowRight size={15} /></Link>
        </Card>
      </div>
    </div>
  );
}
